import z from "zod";
import { UserSubscriptionStatusEnum } from "rdx/modules/users/enums";
import { TeamUserTypeEnum } from "./enums";
import { AvatarSchema } from "../users/schemas/avatar";

export const TeamUserTypeSchema = z.nativeEnum(TeamUserTypeEnum);
export const UserSubscriptionStatusSchema = z.nativeEnum(UserSubscriptionStatusEnum);

export const ParsedQueryParamSchema = z.union([z.string(), z.string().nullable().array(), z.undefined()]).nullable();

export const TeamFilterParamsSchema = z.object({
  activity: ParsedQueryParamSchema,
  certificationTier: ParsedQueryParamSchema,
  enrolledFrom: ParsedQueryParamSchema,
  enrolledTo: ParsedQueryParamSchema,
  enrollment: ParsedQueryParamSchema,
  teamActivity: ParsedQueryParamSchema,
  level: ParsedQueryParamSchema,
  performanceFrom: ParsedQueryParamSchema,
  performanceTo: ParsedQueryParamSchema,
});

export const AmbassadorFilterParamsSchema = z.object({
  level: ParsedQueryParamSchema,
  consultantId: ParsedQueryParamSchema,
  teamActivity: ParsedQueryParamSchema,
  sort: ParsedQueryParamSchema,
  q: ParsedQueryParamSchema,
});

export const TeamSellerSchema = z.object({
  activeProjects: z.object({
    preSalesCount: z.number(),
    postSalesCount: z.number(),
  }),
  address: z.object({ city: z.string().nullable(), state: z.string().nullable() }).optional(),
  avatar: AvatarSchema,
  certificationTier: z.number().nullable(),
  hierarchy: z.array(z.number()),
  email: z.string(),
  enrolledAt: z.string().nullable(),

  firstName: z.string(),
  fullName: z.string(),
  hasViewableDownline: z.boolean(),
  id: z.number(),
  lastName: z.string(),
  performanceMetrics: z.object({
    buildableProjectsCount: z.number(),
    contractCount: z.number(),
    installCount: z.number(),
    recruitCount: z.number(),
    teamCount: z.number(),
  }),
  phone: z.string(),
  relatedLevel: z.number(),
  seatId: z.number().nullable(),
  sponsor: z.object({
    id: z.number(),
    fullName: z.string(),
  }),
  subscriptionStatus: UserSubscriptionStatusSchema,
  userActivity: z.object({
    lastLeadAt: z.string().nullable(),
    lastContractAt: z.string().nullable(),
    lastRecruitAt: z.string().nullable(),
  }),
  userTypes: z.array(TeamUserTypeSchema),
  isSalesPro: z.boolean(),
});

export const TeamAmbassadorSchema = z.object({
  avatar: AvatarSchema,
  email: z.string(),
  firstName: z.string(),
  fullName: z.string(),
  id: z.number(),
  phone: z.string(),
  metrics: z.object({
    recruitCount: z.number(),
    teamCount: z.number(),
    downlevelCount: z.number(),
    projectCount: z.number(),
    qualifyCount: z.number(),
    contractCount: z.number(),
    installCount: z.number(),
    buildableCount: z.number(),
    mentorProjectCount: z.number(),
    mentorQualifyCount: z.number(),
    mentorContractCount: z.number(),
    mentorInstallCount: z.number(),
    referrerProjectCount: z.number(),
    referrerQualifyCount: z.number(),
    referrerContractCount: z.number(),
    referrerInstallCount: z.number(),
  }),
  sponsor: z.object({
    id: z.number(),
    fullName: z.string(),
  }),
  sponsors: z.array(z.number()),
});

export const GetTeamSellersParamsSchema = z.object({
  filterParams: TeamFilterParamsSchema,
  withDownlineExpansion: z.boolean(),
  searchedUserId: z.union([z.number(), z.null()]),
});

export const GetTeamDownlineParamsSchema = z.object({
  filterParams: TeamFilterParamsSchema,
  userId: z.number(),
  searchedUserId: z.union([z.number(), z.null()]),
});

export const GetMoreTeamSellersParamsSchema = z.object({
  filterParams: TeamFilterParamsSchema,
  withDownlineExpansion: z.boolean(),
  p: z.number(),
});

export const GetTeamSellersResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    paging: z.object({
      itemCount: z.number().nullable(),
      pageNumber: z.number(),
      pageSize: z.number(),
      hasMore: z.boolean(),
    }),
    users: z.array(TeamSellerSchema),
  }),
});

export const GetTeamAmbassadorsResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    paging: z.object({
      itemCount: z.number().nullable(),
      pageNumber: z.number(),
      pageSize: z.number(),
      hasMore: z.boolean(),
    }),
    users: z.array(TeamAmbassadorSchema),
  }),
});

export const GetTeamDownlineResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    users: z.array(TeamSellerSchema),
  }),
});

export const GetTeamAmbassadorsParamsSchema = z.object({
  filterParams: AmbassadorFilterParamsSchema,
});

export const GetMoreTeamAmbassadorsParamsSchema = z.object({
  filterParams: AmbassadorFilterParamsSchema,
  p: z.number(),
});

export const TeamLeadFilterParamsSchema = z.object({
  roleTag: z.string().optional(),
  q: z.string().optional(),
  status: z.string().optional(),
});

export const TeamLeadSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  confirmed: z.boolean().nullable().optional(),
  inviteStatus: z.string().nullable().optional(),
  unresponsiveAt: z.string().nullable().optional(),
  level: z.number().nullable().optional(),
  roleTags: z.array(z.string()).optional(),
  address: z
    .object({
      city: z.string().nullable().optional(),
      state: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  sponsor: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable()
    .optional(),
});

export const GetTeamInvitesResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    paging: z.object({
      itemCount: z.number(),
      pageNumber: z.number(),
      pageSize: z.number(),
      hasMore: z.boolean(),
    }),
    leads: z.array(TeamLeadSchema),
  }),
});

export const GetTeamInvitesParamsSchema = z.object({
  filterParams: TeamLeadFilterParamsSchema,
});

export const GetMoreTeamInvitesParamsSchema = z.object({
  filterParams: TeamLeadFilterParamsSchema,
  p: z.number(),
});
